import type { PropsWithChildren } from 'react';
import React, { useCallback, useMemo, useState } from 'react';

import { ErrorBanner } from './error-banner';
import { SuccessBanner } from './success-banner';

type BannerOptions = {
  bannerAction?: () => void;
  bannerActionMessage?: string;
};

type BannerContextValue = {
  showSuccessBanner: (message: string, options?: BannerOptions) => void;
  showErrorBanner: (message: string) => void;
};

export type BannerState = {
  message: string;
  visible: boolean;
};

export type ActionBannerState = BannerState & {
  action?: () => void;
  actionMessage?: string;
};

const BannerContext = React.createContext<BannerContextValue | undefined>(
  undefined
);

export const BannerContextProvider: React.FC<
  PropsWithChildren<{ disableFocusTrap?: boolean }>
> = ({ disableFocusTrap, children }) => {
  const [successState, setSuccessState] = useState<ActionBannerState>({
    message: '',
    visible: false,
  });
  const [errorState, setErrorState] = useState<BannerState>({
    message: '',
    visible: false,
  });

  const hideSuccessBanner = useCallback((): void => {
    setSuccessState({ message: '', visible: false });
  }, []);
  const hideErrorBanner = useCallback((): void => {
    setErrorState({ message: '', visible: false });
  }, []);

  const hidePreviousBanner = useCallback((): void => {
    hideSuccessBanner();
    hideErrorBanner();
  }, [hideErrorBanner, hideSuccessBanner]);

  const showSuccessBanner = useCallback(
    (message: string, options: BannerOptions | undefined) => {
      const {
        bannerAction,
        bannerActionMessage = 'JOBS_MYJOBS_JOB_BANNER_MESSAGE_UNDO',
      } = options ?? {};
      hidePreviousBanner();
      // wait to show the new message once the previous is hidden
      setTimeout(() => {
        setSuccessState({
          message,
          visible: true,
          action: bannerAction,
          actionMessage: bannerActionMessage,
        });
      }, 0);
    },
    [hidePreviousBanner]
  );

  const showErrorBanner = useCallback(
    (message: string) => {
      hidePreviousBanner();
      // wait to show the new message once the previous is hidden
      setTimeout(() => {
        setErrorState({ message, visible: true });
      }, 0);
    },
    [hidePreviousBanner]
  );

  const value = useMemo<BannerContextValue>(
    () => ({
      showSuccessBanner,
      showErrorBanner,
    }),
    [showErrorBanner, showSuccessBanner]
  );

  return (
    <BannerContext.Provider value={value}>
      {children}
      <SuccessBanner
        successState={successState}
        handleOnClose={hideSuccessBanner}
        disableFocusTrap={disableFocusTrap}
      />
      <ErrorBanner
        errorState={errorState}
        handleOnClose={hideErrorBanner}
        disableFocusTrap={disableFocusTrap}
      />
    </BannerContext.Provider>
  );
};

export const useBannerContext = (): BannerContextValue => {
  const context = React.useContext(BannerContext);
  if (!context) {
    throw new Error(
      'useBannerContext must be used within a BannerContextProvider'
    );
  }
  return context;
};
